import {_, React, observer, observable, computed, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource, toJS, Fragment} from '@api'
import {Button, Icon, Menu} from '@blueprintjs/core'
import {Popover2} from '@api'
import {KeyboardArrowRight} from '@material-ui/icons'
import {CatalogItemPreviewMenuItem, CatalogItemSidebarDetailsStore, ContextMenuItem, dataMapperConfig, DetailsModalNavToolbarStore, ICatalogItemDetailsPathElement, useDetailsModalNavToolbarStore, useStores, useVipLocalStore, VipLocalStore} from '@modules'
import {IDataMapConfig, ImageRecord, IWorkspaceForeignKeyDescriptor, StoresInstance, Workspace} from '@stores'
import {LabeledButton, ListPreviewCard, MyImage} from 'centric-ui-components'
import {autorun, runInAction, when} from 'mobx'
import {IDisposer} from 'mobx-utils'
import * as css from './DetailsNavItem.css'

/// region Store

export class DetailsNavItemStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Partial<MyProps>) {
    super(stores)
  }

  registerEffects = () => {
    const toDispose: Array<IDisposer> = []

    autorun(() => {
      const {detailsStore, index} = this
      if (detailsStore) {
        detailsStore.sidebar.navItems[index] = this
      }
    })
    // console.log(`Mount DetailsNavItemStore ${this.props.item?.catalogField}`)

    return () => {
      toDispose.forEach(d => d())
      // console.log(`Unmount DetailsNavItemStore ${this.props.item?.imageId}`)
    }
  }

  get sidebar() { return this.stores.sidebar.right}

  @computed get selected() {
    return this.sidebar.selectedPathIndex === this.index
  }

  @computed get index() {
    return this.props.index
  }

  @computed get siblingItems() {
    const {item: current, sidebar: {navItems}, index} = this,
      parentStore = navItems[index - 1]

    if (!parentStore?.item?.catalogField || !current?.catalogField || !parentStore.data) return null

    const field = current.catalogField.substring(parentStore.item.catalogField.length + 1),
      values = _.get(parentStore.data, field)

    return values
  }

  @action selectNavItem = () => {
    this.sidebar.selectedPathIndex = this.index
  }

  @computed get detailsStore(): CatalogItemSidebarDetailsStore {
    const {index, sidebar: {navDetailStores}} = this
    return index < navDetailStores.length ? navDetailStores[index] : null
  }

  get data() { return this.detailsStore?.data}

  get item(): ICatalogItemDetailsPathElement { return this.props.item }

  @computed get label() {
    const {workspace, catalog, catalog: {labelForFieldName_currentCatalog}, item} = this
    if (workspace) {
      return workspace.catalog.catalogItemName
    }
    else if (item.catalogField) {
      return labelForFieldName_currentCatalog(item.catalogField)
      // const field = _fieldByName[item.catalogField]
      // if (!field) return strings.emptyLabel
      //
      // return workspaceForField(field)?.itemName ?? labelForField(field)
    } else {
      return catalog.catalogItemName
    }
  }

  @computed get id() { return this.item.where[0].value}

  @computed get field() {
    const {catalog: {_fieldByName}, item} = this
    const field = _fieldByName[item.catalogField]
    return field
  }

  @computed get section() {
    const {field, catalog} = this
    const section = catalog.sectionForField(field)
    return section
  }

  @computed get library() {
    const {section, catalog, field} = this
    return catalog.libraryForField(field)
  }

  @computed get workspace(): Workspace {
    const {item: {esIndex}, library, field, workspaces} = this
    if (esIndex) return _.first(workspaces.workspaceByIndex[esIndex])
    if (library) return _.first(workspaces.workspaceByIndex[library.index])
    if (field?.esIndex) return _.first(workspaces.workspaceByIndex[field.esIndex])

    return this.catalog.workspace
  }

  @computed get dataMap(): IDataMapConfig {
    return this.workspace?.dataMap ?? dataMapperConfig.specDataMapperConfig
  }

  @computed get title() {
    const {dataMap, data} = this
    return _.get(data, dataMap?.modalTitle ?? dataMap?.title ?? 'name') ?? this.item.label
  }

  @computed get currentTextValue() {
    const {strings, title, detailsStore: {hasLoadedData}} = this
    return title ?? (hasLoadedData ? strings.noValue : '')
  }

  @computed get readonly() {
    const {index, field, siblingItems} = this
    return index === 0 || field?.def?.type !== 'RefList' || _.isEmpty(siblingItems)
  }

  @computed get images() : Array<ImageRecord> {
    const {dataMap, data} = this

    return _.get(data, dataMap?.images ?? 'images') ?? []
  }

  @computed get image() {
    const {dataMap, data, catalog: {imageUrlForDataMap}} = this
    return imageUrlForDataMap({data, dataMap})
  }

  @computed get foreignKeys() : Array<IWorkspaceForeignKeyDescriptor & {value: any}> {
    const {workspace, data, es} = this
    const fk = workspace?.foreignKeys ?? []

    return fk.map(f => ({...f, value: es.readField(data, f.field.field)?.val }))
  }
}

export const DetailsNavItemStoreContext = React.createContext<DetailsNavItemStore>(null)
export const useDetailsNavItemStore = () => React.useContext(DetailsNavItemStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string,
  index: number
  item?: ICatalogItemDetailsPathElement
}

export const DetailsNavItem = observer<MyProps>(function DetailsNavItem({className, item, index}) {
  const {sidebar:{right: {navItems}}} = useStores(),
    store = navItems[index],
    {sidebar} = store,
    headerExpanded = sidebar?.headerExpanded === true

  useLayoutEffect(() => store.registerEffects(), [])

  if (!store.catalog.hasLoadedConfig || !store.detailsStore) return null

  const {strings, selected, readonly, label, currentTextValue, stores, dataMap, workspace, data, selectNavItem, catalog} = store,
    images = _.get(data, 'images')

  return (
    <DetailsNavItemStoreContext.Provider value={store}>
      <div className={classNames(css.root, className
      )}
        //key={catalog.idFor(data)}
           data-selected={selected}
           data-expanded={headerExpanded}
           data-index={index}
           data-es-index={workspace?.catalog?.index}
           data-id={catalog.idFor(data)}
           onClick={selectNavItem}
      >
        {headerExpanded && <label className={css.label}>{label}{headerExpanded ? '' : ': '}</label>}
        <Popover2
          content={<ChangeDetailsNavItemPathMenu/>}
          position="bottom-left"
          interactionKind="hover"
          hoverOpenDelay={400}
          hoverCloseDelay={300}
          disabled={readonly}
          className={css.popoverWrapper}
          minimal
        >
          {data ? (!headerExpanded ? // Nick's request for a breadcrumb view
                        <Button
                          rightIcon={readonly ? undefined : 'caret-down'}
                          className={css.breadcrumb}
                          minimal
                          text={currentTextValue ?? strings.noValue} />
                        : headerExpanded ?
                   <div className={css.cardWrapper}>
                     <ListPreviewCard
                       stores={stores}
                       className={css.card}
                       data={data} dataMap={_.omit(dataMap, ['subtitle2', 'subtitle3'])} workspace={workspace?.config}
                       hideEmptyImage
                       showTooltip={false}
                       elevation={0}
                     />
                     {readonly ? undefined : <Icon icon="caret-down"/>}
                   </div> : false ? <LabeledButton
              active={selected}
              className={css.button}
              label={label}
              text={
                <div className={css.textContainer}>
                  {false && !_.isEmpty(images) && <MyImage className={css.specImage} imageUrl={{defaultImageUrl: catalog.imageUrlFor(_.first(images))}}/>}
                  <span className={css.text}>{currentTextValue ?? strings.noValue}</span>
                </div>}
              rightIcon={readonly ? undefined :
                         <Icon icon="caret-down"/>
              }
            /> : <div className={css.textContainer}>
                              <span className={css.text}>{currentTextValue ?? strings.noValue}</span>
                            </div>) : <div></div>}
        </Popover2>
      </div>
      {!headerExpanded && (index + 1 < sidebar?.path?.length) && <KeyboardArrowRight className={css.breadcrumbArrow}/>}
    </DetailsNavItemStoreContext.Provider>)
})

const ChangeDetailsNavItemPathMenu = observer(function ChangeDetailsNavItemPathMenu() {
  const {strings, siblingItems, dataMap, item: navItem, data, workspace, catalog} = useDetailsNavItemStore()

  if (_.isEmpty(siblingItems)) return null

  /*
  <ContextMenuItem
            key={i}
            text={text}
            onClick={() => {
              navItem.where[0].value = catalog.idFor(item)
              navItem.label = text
            }}/>

   */

  return (
    <Menu className={css.menu}>
      {siblingItems.map((item, i) => {
        const text = _.get(item, dataMap.title ?? 'name') || strings.noValue
        return (
          <CatalogItemPreviewMenuItem
            key={`${i}_1`} data={item} workspace={workspace?.config}
            className={css.previewItem}
            active={catalog.idFor(data) === catalog.idFor(item)}
            onClick={() => {
              navItem.where[0].value = catalog.idFor(item)
              navItem.label = text
            }}/>
        )
      })}
    </Menu>)
})
/// endregion




// WEBPACK FOOTER //
// ./src/modules/CatalogItemDetails/CatalogItemSidebarDetails/Modal/DetailsNavToolbar/DetailsNavItem.tsx